import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ClipboardDocumentIcon, ExclamationTriangleIcon, UserPlusIcon } from '@heroicons/react/24/outline';
import { ageGroupsAPI, playerApplicationsAPI } from '../services/api';
import { usePageTitle } from '../hooks/usePageTitle';
import { useToast } from '../contexts/ToastContext';
import { useClubSettings } from '../contexts/ClubSettingsContext';
import { useAuth } from '../contexts/AuthContext';
import { isAdmin } from '../types/auth';
import { getErrorMessage } from '../utils/errorMessage';
import type { AgeGroupSelect } from '../types/ageGroup';
import type { PlayerApplication, PlayerApplicationStatus } from '../types/playerApplication';

const FILTERS: { key: PlayerApplicationStatus | 'all'; label: string }[] = [
  { key: 'pending', label: 'Függőben' },
  { key: 'accepted', label: 'Elfogadva' },
  { key: 'rejected', label: 'Elutasítva' },
  { key: 'all', label: 'Összes' },
];

const STATUS_BADGES: Record<PlayerApplicationStatus, { text: string; className: string }> = {
  pending: { text: 'Függőben', className: 'bg-amber-100 text-amber-800' },
  accepted: { text: 'Elfogadva', className: 'bg-green-100 text-green-800' },
  rejected: { text: 'Elutasítva', className: 'bg-red-100 text-red-700' },
};

const age = (birth: string) => {
  const b = new Date(birth);
  const now = new Date();
  let years = now.getFullYear() - b.getFullYear();
  if (now < new Date(now.getFullYear(), b.getMonth(), b.getDate())) years--;
  return years;
};

const Row: React.FC<{ label: string; value?: string | null }> = ({ label, value }) =>
  value ? (
    <div className="flex gap-2 text-sm">
      <span className="w-32 shrink-0 text-gray-500">{label}</span>
      <span className="whitespace-pre-line break-words text-gray-900">{value}</span>
    </div>
  ) : null;

/**
 * Új játékosok jelentkezéseinek elbírálása. Elfogadáskor létrejön a játékos a
 * megadott adatokkal, és a szülő meghívót kap az alkalmazásba.
 */
const PlayerApplications: React.FC = () => {
  usePageTitle('Jelentkezések');
  const { showToast } = useToast();
  const { user } = useAuth();
  const { settings } = useClubSettings();

  const [filter, setFilter] = useState<PlayerApplicationStatus | 'all'>('pending');
  const [applications, setApplications] = useState<PlayerApplication[]>([]);
  const [ageGroups, setAgeGroups] = useState<AgeGroupSelect[]>([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState<number | null>(null);
  const [mode, setMode] = useState<'accept' | 'reject'>('accept');
  const [selectedAgeGroups, setSelectedAgeGroups] = useState<number[]>([]);
  const [inviteParent, setInviteParent] = useState(true);
  const [reason, setReason] = useState('');
  const [notifyParent, setNotifyParent] = useState(true);
  const [busy, setBusy] = useState(false);

  const publicUrl = `${window.location.origin}/apply`;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setApplications(await playerApplicationsAPI.getAll(filter === 'all' ? undefined : filter));
    } catch (err) {
      showToast(getErrorMessage(err, 'A jelentkezéseket nem sikerült betölteni.'), 'error');
    } finally {
      setLoading(false);
    }
  }, [filter, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    ageGroupsAPI.getForSelect().then(setAgeGroups).catch(() => setAgeGroups([]));
  }, []);

  const open = (app: PlayerApplication, nextMode: 'accept' | 'reject') => {
    setOpenId(app.id);
    setMode(nextMode);
    setSelectedAgeGroups(app.suggestedAgeGroupId ? [app.suggestedAgeGroupId] : []);
    setInviteParent(true);
    setReason('');
    setNotifyParent(true);
  };

  const accept = async (app: PlayerApplication) => {
    if (selectedAgeGroups.length === 0) {
      showToast('Válassz korosztályt.', 'error');
      return;
    }
    setBusy(true);
    try {
      const result = await playerApplicationsAPI.accept(app.id, selectedAgeGroups, inviteParent);
      const parentText = result.parentLinked
        ? 'A szülő meglévő fiókjához kapcsoltuk.'
        : result.invitationSent
          ? 'A szülő meghívót kapott.'
          : result.invitationError ?? '';
      showToast(`${app.playerName} felvéve. ${parentText}`, result.invitationError ? 'error' : 'success');
      setOpenId(null);
      await load();
    } catch (err) {
      showToast(getErrorMessage(err, 'Az elfogadás nem sikerült.'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const reject = async (app: PlayerApplication) => {
    setBusy(true);
    try {
      await playerApplicationsAPI.reject(app.id, reason.trim() || undefined, notifyParent);
      showToast('Jelentkezés elutasítva.');
      setOpenId(null);
      await load();
    } catch (err) {
      showToast(getErrorMessage(err, 'Az elutasítás nem sikerült.'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      showToast('Link a vágólapon.');
    } catch {
      showToast('A másolás nem sikerült.', 'error');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Jelentkezések</h1>
          <p className="mt-1 text-sm text-gray-500">Új játékosok jelentkezése a nyilvános űrlapon.</p>
        </div>
      </div>

      {/* A nyilvános link: ezt lehet a weboldalra, plakátra, QR-kódba tenni */}
      <div className="rounded-lg border bg-white p-4 shadow-sm">
        {settings?.applicationsEnabled ? (
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <span className="text-sm font-medium text-gray-700">Jelentkezési link:</span>
            <input
              readOnly
              value={publicUrl}
              onFocus={(e) => e.target.select()}
              className="min-w-0 flex-1 rounded-md border border-gray-300 bg-gray-50 px-3 py-1.5 font-mono text-xs"
            />
            <button
              type="button"
              onClick={copyLink}
              className="inline-flex items-center justify-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
            >
              <ClipboardDocumentIcon className="h-4 w-4" />
              Másolás
            </button>
          </div>
        ) : (
          <p className="text-sm text-gray-600">
            A nyilvános jelentkezési űrlap ki van kapcsolva.
            {isAdmin(user) ? (
              <>
                {' '}
                <Link to="/system-settings" className="font-medium text-primary-700 hover:underline">
                  Bekapcsolás a Rendszerbeállításokban
                </Link>
              </>
            ) : (
              ' Az admin a Rendszerbeállításokban kapcsolhatja be.'
            )}
          </p>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`rounded-full px-3 py-1.5 text-sm font-medium ${
              filter === f.key ? 'bg-primary-600 text-white' : 'bg-white text-gray-700 ring-1 ring-gray-300 hover:bg-gray-50'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">Betöltés...</p>
      ) : applications.length === 0 ? (
        <div className="rounded-lg border bg-white p-8 text-center text-sm text-gray-500">
          <UserPlusIcon className="mx-auto mb-2 h-10 w-10 text-gray-300" />
          Nincs {filter === 'pending' ? 'elbírálásra váró ' : ''}jelentkezés.
        </div>
      ) : (
        <div className="space-y-4">
          {applications.map((app) => {
            const badge = STATUS_BADGES[app.status];
            return (
              <div key={app.id} className="rounded-lg border bg-white p-4 shadow-sm">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-lg font-semibold text-gray-900">{app.playerName}</h3>
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${badge.className}`}>{badge.text}</span>
                      {app.suggestedAgeGroupName && (
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700">{app.suggestedAgeGroupName}</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">
                      {new Date(app.birth).toLocaleDateString('hu-HU')} ({age(app.birth)} éves) · {app.gender === 'Nő' ? 'lány' : 'fiú'} ·
                      jelentkezett {new Date(app.createdAt).toLocaleDateString('hu-HU')}
                    </p>
                  </div>
                </div>

                {app.possibleDuplicate && (
                  <div className="mt-2 flex items-center gap-2 rounded bg-amber-50 px-3 py-2 text-sm text-amber-800">
                    <ExclamationTriangleIcon className="h-4 w-4 shrink-0" />
                    Már van ilyen nevű és születési dátumú játékos - lehet, hogy kétszer jelentkezett.
                  </div>
                )}

                <div className="mt-3 space-y-1">
                  <Row label="Szülő" value={app.parentName} />
                  <Row label="Email" value={app.parentEmail} />
                  <Row label="Telefon" value={app.parentPhone} />
                  <Row label="Születési hely" value={app.placeOfBirth} />
                  <Row label="Lakcím" value={app.address} />
                  <Row label="Játékos telefon" value={app.playerPhone} />
                  <Row
                    label="Vészhelyzet"
                    value={[app.emergencyContactName, app.emergencyContactPhone].filter(Boolean).join(', ') || null}
                  />
                  <Row label="Egészségügy" value={app.medicalNotes} />
                  <Row label="Korábbi klub" value={app.previousClub} />
                  <Row label="Megjegyzés" value={app.note} />
                  {app.termsAcceptedAt && (
                    <Row label="Feltételek" value={`elfogadva ${new Date(app.termsAcceptedAt).toLocaleString('hu-HU')}`} />
                  )}
                  {app.status !== 'pending' && (
                    <Row
                      label="Elbírálta"
                      value={`${app.decidedByUserName ?? ''}${app.decidedAt ? `, ${new Date(app.decidedAt).toLocaleDateString('hu-HU')}` : ''}${
                        app.rejectionReason ? ` – ${app.rejectionReason}` : ''
                      }`}
                    />
                  )}
                </div>

                {app.status === 'accepted' && app.createdPlayerId && (
                  <Link to={`/players/${app.createdPlayerId}`} className="mt-3 inline-block text-sm font-medium text-primary-700 hover:underline">
                    Játékos adatlapja →
                  </Link>
                )}

                {app.status === 'pending' && openId !== app.id && (
                  <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                    <button
                      onClick={() => open(app, 'accept')}
                      className="rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700"
                    >
                      Elfogadás
                    </button>
                    <button
                      onClick={() => open(app, 'reject')}
                      className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      Elutasítás
                    </button>
                  </div>
                )}

                {openId === app.id && mode === 'accept' && (
                  <div className="mt-4 space-y-3 rounded-lg border border-primary-200 bg-primary-50/40 p-3">
                    <p className="text-sm font-medium text-gray-900">Melyik korosztályba kerüljön?</p>
                    <div className="flex flex-wrap gap-2">
                      {ageGroups.map((ag) => {
                        const on = selectedAgeGroups.includes(ag.id);
                        return (
                          <button
                            key={ag.id}
                            type="button"
                            onClick={() =>
                              setSelectedAgeGroups((prev) => (on ? prev.filter((id) => id !== ag.id) : [...prev, ag.id]))
                            }
                            className={`rounded-full px-3 py-1 text-sm ${
                              on ? 'bg-primary-600 text-white' : 'bg-white text-gray-700 ring-1 ring-gray-300'
                            }`}
                          >
                            {ag.name}
                          </button>
                        );
                      })}
                    </div>
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={inviteParent}
                        onChange={(e) => setInviteParent(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-primary-600"
                      />
                      Meghívó a szülőnek az alkalmazásba (ha még nincs fiókja)
                    </label>
                    <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                      <button onClick={() => setOpenId(null)} className="rounded-md bg-gray-100 px-4 py-2 text-sm text-gray-700">
                        Mégse
                      </button>
                      <button
                        disabled={busy}
                        onClick={() => accept(app)}
                        className="rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
                      >
                        {busy ? 'Mentés...' : 'Játékos felvétele'}
                      </button>
                    </div>
                  </div>
                )}

                {openId === app.id && mode === 'reject' && (
                  <div className="mt-4 space-y-3 rounded-lg border border-red-200 bg-red-50/40 p-3">
                    <label className="block text-sm text-gray-700">
                      Indoklás (a szülő is megkapja)
                      <input
                        type="text"
                        maxLength={500}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                        placeholder="Pl. ebben a korosztályban jelenleg nincs hely"
                      />
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={notifyParent}
                        onChange={(e) => setNotifyParent(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-primary-600"
                      />
                      Email a szülőnek az elutasításról
                    </label>
                    <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                      <button onClick={() => setOpenId(null)} className="rounded-md bg-gray-100 px-4 py-2 text-sm text-gray-700">
                        Mégse
                      </button>
                      <button
                        disabled={busy}
                        onClick={() => reject(app)}
                        className="rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
                      >
                        {busy ? 'Mentés...' : 'Elutasítás'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PlayerApplications;
